// ===========================================
// MARGINS — CPG VIEW  (route /margins, CPG accounts)
// ===========================================
// A brand's margins are monthly and per channel, not a POS sales mix:
//   · Channel margin summary — the same figures Wholesale → Channels and the
//     CPG P&L read (/wholesale/summary/categories, fx-labeled server-side)
//   · Product margin table — realized price per unit from wholesale orders ×
//     the cost per case you entered (falling back to Inventory unit cost —
//     each row says which), with units month by month and the margin trend
//   · Per-liter margin where the product name carries a size
// The Forecast tab is the demand side of the same orders: monthly, yearly,
// per channel, per product — see components/CpgForecast.
// Nothing here comes from a POS and nothing is estimated.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendingUp, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { wholesaleApi } from '../services/api';
import EntityToggle, { EntityView } from '../components/EntityToggle';
import { CpgForecastView, useCpgDemand } from '../components/CpgForecast';
import { formatByCurrency } from '../lib/money';
import { fmtMoney, fmtMoneyShort, CATEGORY_CHIP } from '../utils/cpg';
import { ymShort, ymLong, type ProductDemand } from '../lib/cpgDemand';

const Dash = () => <span className="text-slate-300">—</span>;
const n0 = (n: number) => Math.round(n).toLocaleString('en-US');

// Margin % color bands — same thresholds ProductsPage and the CPG P&L use.
const marginTone = (pct: number) => (pct < 20 ? 'text-red-600' : pct < 40 ? 'text-amber-600' : 'text-emerald-700');

interface CategoryRow {
  category: string; label: string; customers: number; orders: number;
  revenueCents: number | null; revenueLabel?: string; cogsCents: number | null; deliveryCents: number | null;
  marginCents: number | null; marginPct: number | null;
}
interface CategorySummary { months: string[]; rows: CategoryRow[]; hasCosts: boolean; source: string; fx?: { label?: string } | null }

type View = 'margins' | 'forecast';

export default function CpgMarginsPage() {
  // Deep links: /margins?view=forecast (the Production outlook points here).
  const urlView = new URLSearchParams(window.location.search).get('view');
  const [view, setView] = useState<View>(urlView === 'forecast' ? 'forecast' : 'margins');
  const [entity, setEntity] = useState<EntityView>('all');
  const [channel, setChannel] = useState<string | null>(null);

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{view === 'margins' ? 'Margins' : 'Forecast'}</h1>
          <p className="text-slate-500 text-sm">
            {view === 'margins'
              ? 'What each product and channel actually earns — monthly, computed from your wholesale orders.'
              : 'Demand by month, channel and product — computed from your wholesale orders.'}
          </p>
        </div>
        <EntityToggle value={entity} onChange={setEntity} />
      </div>

      <div className="flex gap-2">
        {([['margins', 'Margins'], ['forecast', 'Forecast']] as Array<[View, string]>).map(([id, label]) => (
          <button key={id} onClick={() => setView(id)}
            className={clsx('px-4 py-2 rounded-full text-sm font-medium', view === id ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}>
            {label}
          </button>
        ))}
      </div>

      {view === 'forecast'
        ? <CpgForecastView entity={entity} />
        : <CpgMarginsView entity={entity} channel={channel} setChannel={setChannel} />}
    </div>
  );
}

// ===========================================
// MARGINS VIEW
// ===========================================
function CpgMarginsView({ entity, channel, setChannel }: {
  entity: EntityView; channel: string | null; setChannel: (c: string | null) => void;
}) {
  const { model, isPending } = useCpgDemand(entity, channel);

  // Channel-level margins come from the same server summary Channels and the
  // CPG P&L read — fx conversion happens there, at a dated ECB rate.
  const { data: cats } = useQuery<CategorySummary | null>({
    queryKey: ['wholesale-category-summary', entity],
    queryFn: async () => (await wholesaleApi.getCategorySummary(entity === 'all' ? undefined : entity)).data?.data || null,
    staleTime: 60_000,
  });

  // Months shown on the product table: the last six of the model's window
  // (plus booked-ahead months), so the table stays readable.
  const tableMonths = useMemo(() => {
    const past = model.months.filter((m) => m <= model.currentYm);
    return [...past.slice(-6), ...model.futureMonths];
  }, [model]);
  const spansYears = new Set(tableMonths.map((m) => m.slice(0, 4))).size > 1;
  const monthLabel = (ym: string) => ymShort(ym, spansYears);

  if (isPending) {
    return <div className="card p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-600" /><p className="mt-4 text-slate-500">Computing margins from your orders…</p></div>;
  }
  if (model.orderCount === 0) {
    return (
      <div className="card p-10 text-center text-slate-500">
        Margins appear once wholesale orders exist — orders bring the realized price per unit; the cost
        comes from your cost per case (Wholesale → Channels) or the Inventory unit cost.
      </div>
    );
  }

  const catRows = (cats?.rows || []).filter((r) => (r.revenueCents || 0) > 0 || r.orders > 0);
  const hasNonUsd = Object.keys(model.nonUsdByCurrency).length > 0;

  // Margin % for one month of one product — realized USD price that month vs its cost.
  const monthMarginPct = (x: ProductDemand, m: string): number | null => {
    if (x.costCents == null) return null;
    const rev = x.usdRevenueByMonth[m] || 0;
    const u = x.usdUnitsByMonth[m] || 0;
    if (rev <= 0 || u <= 0) return null;
    return ((rev - u * x.costCents) / rev) * 100;
  };
  // Trend: the last two complete months that actually have USD sales.
  const trendOf = (x: ProductDemand) => {
    const withData = model.months.filter((m) => m < model.currentYm && monthMarginPct(x, m) != null);
    if (withData.length < 2) return null;
    const [prevM, lastM] = withData.slice(-2);
    const prev = monthMarginPct(x, prevM)!;
    const last = monthMarginPct(x, lastM)!;
    return { prevM, lastM, prev, last, delta: last - prev };
  };

  const costed = model.products.filter((x) => x.costCents != null);
  const uncosted = model.products.filter((x) => x.costCents == null);

  return (
    <div className="space-y-5">
      {/* Channel margin summary — server-computed, fx-labeled */}
      <div className="card p-0 overflow-x-auto">
        <div className="px-4 pt-4 pb-2">
          <h2 className="text-lg font-semibold text-slate-900">Margin by channel</h2>
          <p className="text-sm text-slate-500">Revenue, cost per case and margin per sales category — the same figures the CPG P&amp;L builds on.</p>
        </div>
        {cats === undefined ? (
          <p className="text-sm text-slate-500 px-4 pb-4">Reading the channel summary…</p>
        ) : catRows.length === 0 ? (
          <p className="text-sm text-slate-500 px-4 pb-4">No counted orders in a sales category yet — set each customer's category on Wholesale → Customers.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-slate-400 border-b border-slate-100">
                <th className="px-4 py-2 font-medium text-left">Channel</th>
                <th className="px-4 py-2 font-medium text-right">Accounts</th>
                <th className="px-4 py-2 font-medium text-right">Orders</th>
                <th className="px-4 py-2 font-medium text-right">Revenue</th>
                <th className="px-4 py-2 font-medium text-right">COGS</th>
                <th className="px-4 py-2 font-medium text-right">Delivery</th>
                <th className="px-4 py-2 font-medium text-right">Margin</th>
                <th className="px-4 py-2 font-medium text-right">Margin %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {catRows.map((r) => (
                <tr key={r.category}>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <span className={clsx('px-1.5 py-0.5 rounded text-xs font-medium', CATEGORY_CHIP[r.category] || 'bg-slate-100 text-slate-600')}>{r.label}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{r.customers}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{r.orders}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-medium text-slate-900">{r.revenueCents != null ? fmtMoneyShort(r.revenueCents) : <Dash />}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{r.cogsCents != null ? fmtMoneyShort(r.cogsCents) : <Dash />}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{r.deliveryCents ? fmtMoneyShort(r.deliveryCents) : <Dash />}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-slate-900">{r.marginCents != null ? fmtMoneyShort(r.marginCents) : <Dash />}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {r.marginPct != null ? <span className={clsx('font-medium', marginTone(r.marginPct))}>{r.marginPct}%</span> : <Dash />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="text-[11px] text-slate-400 px-4 py-2.5 border-t border-slate-50">
          {cats?.source || 'Computed from counted wholesale orders.'}
          {cats && !cats.hasCosts && ' COGS and margin fill in once you enter cost per case on Wholesale → Channels — nothing is estimated in the meantime.'}
          {cats?.fx?.label ? ` ${cats.fx.label}` : ''}
        </p>
      </div>

      {/* Product margins, monthly */}
      <div className="card p-0">
        <div className="px-4 pt-4 pb-2 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Margin by product</h2>
            <p className="text-sm text-slate-500">Realized price per unit from wholesale orders × your cost — with units sold month by month.</p>
          </div>
          {model.channels.length > 1 && (
            <div className="flex gap-1.5 flex-wrap">
              <button onClick={() => setChannel(null)}
                className={clsx('px-2.5 py-1 rounded-full text-xs font-medium', channel === null ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}>
                All channels
              </button>
              {model.channels.map((c) => (
                <button key={c.category} onClick={() => setChannel(channel === c.category ? null : c.category)}
                  className={clsx('px-2.5 py-1 rounded-full text-xs font-medium', channel === c.category ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}>
                  {c.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {hasNonUsd && (
          <p className="text-xs text-slate-500 px-4 pb-2">
            {formatByCurrency(model.nonUsdByCurrency)} of these orders bill in their own currency. Units count everywhere;
            price and margin math stays in USD (your costs are USD) — converted channel totals live in the summary above, at a dated ECB rate.
          </p>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-slate-400 border-b border-slate-100">
                <th className="px-4 py-2 font-medium text-left min-w-[180px]">Product</th>
                <th className="px-3 py-2 font-medium text-right" title="Average realized price per unit on USD wholesale orders in this window">Price</th>
                <th className="px-3 py-2 font-medium text-right" title="Cost per case from Wholesale → Channels, or the Inventory unit cost — the badge says which">Cost</th>
                <th className="px-3 py-2 font-medium text-right">Margin</th>
                <th className="px-3 py-2 font-medium text-right">Margin %</th>
                <th className="px-3 py-2 font-medium text-right" title="Margin per liter, where the product name carries a size">$/L margin</th>
                {tableMonths.map((m) => (
                  <th key={m} className="px-2 py-2 font-medium text-right whitespace-nowrap" title={`Units sold, ${ymLong(m)}`}>{monthLabel(m)}{m > model.currentYm ? '†' : ''}</th>
                ))}
                <th className="px-3 py-2 font-medium text-right border-l border-slate-200">Units</th>
                <th className="px-3 py-2 font-medium text-right" title="Margin % change between your last two complete months with USD sales">Trend</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {model.products.length === 0 && (
                <tr>
                  <td colSpan={9 + tableMonths.length} className="px-4 py-6 text-center text-sm text-slate-500">
                    No counted orders in this channel yet — pick another, or All channels.
                  </td>
                </tr>
              )}
              {model.products.map((x) => {
                const avgPrice = x.usdUnits > 0 ? Math.round(x.usdRevenueCents / x.usdUnits) : null;
                const margin = avgPrice != null && x.costCents != null ? avgPrice - x.costCents : null;
                const marginPct = margin != null && avgPrice! > 0 ? (margin / avgPrice!) * 100 : null;
                const perLiter = margin != null && x.litersEach ? Math.round(margin / x.litersEach) : null;
                const trend = trendOf(x);
                return (
                  <tr key={x.key}>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className="font-medium text-slate-900">{x.name}</span>
                      {x.sku && <span className="text-xs text-slate-400 ml-1.5">SKU {x.sku}</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-900">{avgPrice != null ? fmtMoney(avgPrice) : <Dash />}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      {x.costCents != null ? (
                        <span className="tabular-nums text-slate-600">
                          {fmtMoney(x.costCents)}
                          <span className="text-[10px] text-slate-400 ml-1" title={x.costSource === 'case' ? 'Your cost per case, from Wholesale → Channels' : 'Unit cost from Inventory'}>
                            {x.costSource === 'case' ? 'case cost' : 'inventory'}
                          </span>
                        </span>
                      ) : <Dash />}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-900">{margin != null ? fmtMoney(margin) : <Dash />}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {marginPct != null ? <span className={clsx('font-medium', marginTone(marginPct))}>{Math.round(marginPct)}%</span> : <Dash />}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{perLiter != null ? `${fmtMoney(perLiter)}/L` : <Dash />}</td>
                    {tableMonths.map((m) => (
                      <td key={m} className="px-2 py-2.5 text-right tabular-nums text-slate-600">
                        {x.unitsByMonth[m] ? n0(x.unitsByMonth[m]) : <Dash />}
                      </td>
                    ))}
                    <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-slate-900 border-l border-slate-200">{n0(x.unitsTotal)}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      {trend ? (
                        <span
                          className={clsx('text-xs font-medium tabular-nums', trend.delta > 0.5 ? 'text-emerald-600' : trend.delta < -0.5 ? 'text-red-600' : 'text-slate-500')}
                          title={`${ymShort(trend.prevM, true)} ${Math.round(trend.prev)}% → ${ymShort(trend.lastM, true)} ${Math.round(trend.last)}%`}
                        >
                          {trend.delta > 0 ? '+' : ''}{trend.delta.toFixed(1)} pts
                        </span>
                      ) : <Dash />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-400 px-4 py-2.5 border-t border-slate-50">
          Computed from counted wholesale orders{channel ? ` in the ${model.channels.find((c) => c.category === channel)?.label || channel} channel` : ''} over the shown window
          {model.futureMonths.length > 0 ? ' († months are booked ahead — confirmed orders with a future fulfillment date)' : ''}.
          Price is the realized average per unit, not the list price. Cost: your cost per case where entered, otherwise the Inventory unit cost — each row says which.
          {uncosted.length > 0 && ` ${uncosted.length} product${uncosted.length === 1 ? ' has' : 's have'} no cost on file (${uncosted.slice(0, 4).map((x) => x.name).join(', ')}${uncosted.length > 4 ? '…' : ''}) — margins stay blank, never guessed.`}
          {costed.length === 0 && ' Enter cost per case on Wholesale → Channels (or a unit cost in Inventory) and margins fill in.'}
        </p>
      </div>

      <p className="text-xs text-slate-400 flex items-center gap-1.5">
        <TrendingUp className="w-3.5 h-3.5" />
        Everything on this page is computed from wholesale orders — no POS required, nothing estimated.
      </p>
    </div>
  );
}
